import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, LayoutGrid, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { api, type CategoryFacet } from "@/api";
import { Skeleton } from "@osmix/ui/skeleton";

// Каталог-лендинг «как в приложении»: не сетка всех товаров, а СПИСОК категорий
// (мобильный CatalogHomeCategoryList / «одежда» body). Строка = миниатюра +
// название + счётчик, ведёт в фид этой категории (?categoryId=). Сверху «Все
// товары», снизу «Идеи для вдохновения». Обложки категорий берём из реальной
// выдачи каталога (первое фото товара из поддерева) — гейтвей картинок категорий
// не отдаёт, поэтому ничего не выдумываем: нет товара — нейтральный плейсхолдер.

// Все id узла и его потомков — для сопоставления товара с категорией верхнего
// уровня (facet.count уже свёрнут по поддереву, отдельно считать не нужно).
function collectSubtreeIds(node: CategoryFacet): string[] {
  const ids = [node.value];
  for (const child of node.children ?? []) ids.push(...collectSubtreeIds(child));
  return ids;
}
export type CatalogCategoryListProps = {
  // Верхнеуровневые категории отдела (уже отфильтрованы под «Одежда»/«Дом»).
  categories: CategoryFacet[];
  // Ссылка строки «Все товары» (фид отдела целиком) и обработчик выбора.
  onSelectCategory: (categoryId: string) => void;
  onSelectAll: () => void;
  // id корня отдела, чтобы обложки брались из товаров этого отдела.
  departmentRootId?: string;
  // Картинки категорий из дерева (imageUrl, задаётся в консоли). Есть — она
  // важнее обложки из товаров; нет — прежняя обложка/заглушка.
  imageById?: ReadonlyMap<string, string>;
};
export function CatalogCategoryList(t0) {
  const $ = _c(40);
  const {
    categories,
    onSelectCategory,
    onSelectAll,
    departmentRootId,
    imageById
  } = t0;
  const {
    t
  } = useTranslation();
  const t1 = departmentRootId ?? "all";
  let t2;
  if ($[0] !== t1) {
    t2 = ["catalog", "category-covers", t1];
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== departmentRootId) {
    t3 = () => api.products.list(departmentRootId ? {
      categoryId: departmentRootId,
      pageSize: 60,
      sort: "newest"
    } : {
      pageSize: 60,
      sort: "newest"
    });
    $[2] = departmentRootId;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const covers = useQuery(t4);
  let map;
  if ($[7] !== categories || $[8] !== covers.data?.data) {
    map = new Map();
    let t5;
    if ($[10] !== covers.data?.data) {
      t5 = covers.data?.data ?? [];
      $[10] = covers.data?.data;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    const products = t5;
    for (const category of categories) {
      const ids = new Set(collectSubtreeIds(category));
      const hit = products.find(p => ids.has(p.categoryId) && p.photos?.[0]);
      const cover = hit?.photos?.[0];
      if (cover) {
        map.set(category.value, cover);
      }
    }
    $[7] = categories;
    $[8] = covers.data?.data;
    $[9] = map;
  } else {
    map = $[9];
  }
  const coverByCategory = map;
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="flex size-16 shrink-0 items-center justify-center rounded-[4px] bg-osmix-gray-100 text-osmix-icon"><LayoutGrid className="size-6" strokeWidth={1.5} /></span>;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t) {
    t6 = t("catalog.allProducts");
    $[13] = t;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t6) {
    t7 = <span className="min-w-0 flex-1 type-body1 text-osmix-text-1 group-hover:text-osmix-tangelo">{t6}</span>;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ChevronRight className="size-5 shrink-0 text-osmix-icon" />;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== onSelectAll || $[19] !== t7) {
    t9 = <li><button type="button" onClick={onSelectAll} className="group flex w-full items-center gap-4 rounded-[4px] py-2.5 pr-2 text-left outline-none transition-colors hover:bg-osmix-gray-100 focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40">{t5}{t7}{t8}</button></li>;
    $[18] = onSelectAll;
    $[19] = t7;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== categories || $[22] !== coverByCategory || $[23] !== covers.isPending || $[24] !== imageById || $[25] !== onSelectCategory || $[26] !== t) {
    t10 = covers.isPending && categories.length === 0 ? ["a", "b", "c", "d", "e", "f"].map(_temp) : categories.map(category_0 => {
      const cover_0 = imageById?.get(category_0.value) ?? coverByCategory.get(category_0.value);
      return <li key={category_0.value}><button type="button" onClick={() => onSelectCategory(category_0.value)} className="group flex w-full items-center gap-4 rounded-[4px] py-2.5 pr-2 text-left outline-none transition-colors hover:bg-osmix-gray-100 focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40"><span className="size-16 shrink-0 overflow-hidden rounded-[4px] bg-osmix-gray-100">{cover_0 && <img src={cover_0} alt="" loading="lazy" className="size-full object-cover transition-transform duration-300 group-hover:scale-[1.04]" />}</span><span className="min-w-0 flex-1"><span className="block truncate type-body1 text-osmix-text-1 group-hover:text-osmix-tangelo">{category_0.label}</span>{category_0.count > 0 && <span className="block type-body2 text-osmix-icon">{t("catalog.categoryItems", {
                defaultValue: "{{count}} \u0442\u043E\u0432\u0430\u0440\u043E\u0432",
                count: category_0.count
              })}</span>}</span><ChevronRight className="size-5 shrink-0 text-osmix-icon" /></button></li>;
    });
    $[21] = categories;
    $[22] = coverByCategory;
    $[23] = covers.isPending;
    $[24] = imageById;
    $[25] = onSelectCategory;
    $[26] = t;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span className="flex size-16 shrink-0 items-center justify-center rounded-[4px] bg-osmix-tangelo/10 text-osmix-tangelo"><Sparkles className="size-6" strokeWidth={1.5} /></span>;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t) {
    t12 = t("catalog.inspiration", {
      defaultValue: "\u0418\u0434\u0435\u0438 \u0434\u043B\u044F \u0432\u0434\u043E\u0445\u043D\u043E\u0432\u0435\u043D\u0438\u044F"
    });
    $[29] = t;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t12) {
    t13 = <span className="min-w-0 flex-1 type-body1 text-osmix-text-1 group-hover:text-osmix-tangelo">{t12}</span>;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ChevronRight className="size-5 shrink-0 text-osmix-icon" />;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t13) {
    t15 = <li><Link to="/feed" className="group flex w-full items-center gap-4 rounded-[4px] py-2.5 pr-2 text-left outline-none transition-colors hover:bg-osmix-gray-100 focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40">{t11}{t13}{t14}</Link></li>;
    $[34] = t13;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t10 || $[37] !== t15 || $[38] !== t9) {
    t16 = <ul className="flex flex-col">{t9}{t10}{t15}</ul>;
    $[36] = t10;
    $[37] = t15;
    $[38] = t9;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  return t16;
}
function _temp(k) {
  return <li key={k} className="flex items-center gap-4 py-2.5"><Skeleton className="size-16 rounded-[4px]" /><Skeleton className="h-4 w-40" /></li>;
}